import { ContentReportActions } from "./ContentReportActions";
import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock } from 'lucide-react';
import { useAuth } from '../../packages/auth-client';
import { apiClient } from '../../packages/api-client';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageHeader, Table, StateView, Drawer, type Column } from '../../packages/ui';
import {
    adminListTickets, adminUpdateTicket,
    type SupportTicket, type TicketStatus, type TicketPriority, type TicketCategory, type AdminUpdateInput,
} from '../../packages/api-client/support-api';
import './platformOps.css';

export const SUPPORT_PAGE_SIZE = 25;

const STATUSES: { value: TicketStatus; label: string; tone: string }[] = [
    { value: 'open', label: 'Open', tone: 'ops-pill ops-pill--danger' },
    { value: 'in_progress', label: 'In progress', tone: 'ops-pill ops-pill--warn' },
    { value: 'resolved', label: 'Resolved', tone: 'ops-pill ops-pill--ok' },
    { value: 'closed', label: 'Closed', tone: 'ops-pill' },
];
const PRIORITIES: { value: TicketPriority; label: string }[] = [
    { value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'High' },
];
const CATEGORIES: { value: TicketCategory; label: string }[] = [
    { value: 'question', label: 'Question' }, { value: 'bug', label: 'Bug' }, { value: 'feature', label: 'Feature' },
    { value: 'billing', label: 'Billing' }, { value: 'other', label: 'Other' },
];
const statusMeta = (s: TicketStatus) => STATUSES.find(x => x.value === s) ?? STATUSES[0];
const label = (list: { value: string; label: string }[], v: string) => list.find(x => x.value === v)?.label ?? v;

type Row = SupportTicket & Record<string, unknown>;
type Load = { kind: 'loading' } | { kind: 'error'; denied: boolean } | { kind: 'ok'; tickets: Row[]; total: number };

export function SupportInboxPage() {
    usePageTitle('Support inbox');
    const { isSuperAdmin } = useAuth();
    const allowed = isSuperAdmin();
    const [params, setParams] = useSearchParams();
    const [load, setLoad] = useState<Load>({ kind: 'loading' });
    const [reload, setReload] = useState(0);
    const [tenants, setTenants] = useState<Map<string, string> | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // The queue opens on open tickets, as before; ?status=all shows every status.
    const statusParam = params.get('status') ?? 'open';
    const status = statusParam === 'all' ? '' : statusParam as TicketStatus;
    const priority = (params.get('priority') ?? '') as TicketPriority | '';
    const category = (params.get('category') ?? '') as TicketCategory | '';
    const tenantId = params.get('tenant') ?? '';
    const requester = params.get('requester') ?? '';
    const page = Math.max(1, Number(params.get('page')) || 1);
    // Anything narrower than the default open queue or the full list.
    const narrowed = Boolean(priority || category || tenantId || requester || page > 1 || (status && status !== 'open'));
    const query = params.toString();

    useEffect(() => {
        if (!allowed) return;
        let live = true;
        setLoad({ kind: 'loading' });
        adminListTickets({ status, priority, category, tenant_id: tenantId, requester, page, limit: SUPPORT_PAGE_SIZE })
            .then(r => { if (live) setLoad({ kind: 'ok', tickets: r.data as Row[], total: r.total }); })
            .catch(err => { if (live) setLoad({ kind: 'error', denied: axios.isAxiosError(err) && err.response?.status === 403 }); });
        return () => { live = false; };
        // `query` captures every filter and the page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowed, query, reload]);

    // Tenant names for display and the tenant filter. Without them the queue
    // still works; tenants show by id and the tenant filter is not offered.
    useEffect(() => {
        if (!allowed) return;
        apiClient.get<{ data: { id: string; name: string }[] }>('/tenants')
            .then(r => setTenants(new Map((r.data.data ?? []).map(t => [t.id, t.name]))))
            .catch(() => setTenants(null));
    }, [allowed]);

    const setFilter = useCallback((key: string, value: string) => {
        setParams(prev => {
            const next = new URLSearchParams(prev);
            if (value) next.set(key, value); else next.delete(key);
            next.delete('page');
            return next;
        }, { replace: true });
    }, [setParams]);
    const goToPage = (p: number) => setParams(prev => {
        const next = new URLSearchParams(prev);
        if (p > 1) next.set('page', String(p)); else next.delete('page');
        return next;
    });
    // The requester box updates the URL (and so the request) 300ms after typing stops.
    const [requesterDraft, setRequesterDraft] = useState(requester);
    useEffect(() => { setRequesterDraft(requester); }, [requester]);
    useEffect(() => {
        if (requesterDraft === requester) return;
        const id = setTimeout(() => setFilter('requester', requesterDraft.trim()), 300);
        return () => clearTimeout(id);
    }, [requesterDraft, requester, setFilter]);
    const clearFilters = () => setParams(new URLSearchParams({ status: 'all' }), { replace: true });

    if (!allowed || (load.kind === 'error' && load.denied)) {
        return (
            <div className="ops-page">
                <PageHeader title="Support inbox" />
                <StateView state="denied" reason="The support inbox is limited to platform superadmins."
                    nextStep="Customers see their own tickets under Help & Support." />
            </div>
        );
    }

    const tenantName = (id?: string | null) => (id ? tenants?.get(id) ?? id : 'No tenant (platform user)');
    const onSaved = (t: SupportTicket) => setLoad(prev => prev.kind === 'ok'
        ? { ...prev, tickets: prev.tickets.map(x => (x.id === t.id ? t as Row : x)) }
        : prev);
    const selected = load.kind === 'ok' ? load.tickets.find(t => t.id === selectedId) ?? null : null;

    const columns: Column<Row>[] = [
        {
            key: 'subject', header: 'Ticket', rowHeader: true,
            render: t => <>{t.subject}<span className="ops-sub">{t.user_email}</span></>,
        },
        { key: 'tenant', header: 'Tenant', render: t => <span className={tenants?.get(t.tenant_id ?? '') ? '' : 'ops-mono'}>{tenantName(t.tenant_id)}</span> },
        { key: 'category', header: 'Category', render: t => label(CATEGORIES, t.category) },
        { key: 'status', header: 'Status', render: t => <span className={statusMeta(t.status).tone}>{statusMeta(t.status).label}</span> },
        { key: 'priority', header: 'Priority', render: t => <span className={t.priority === 'high' ? 'ops-pill ops-pill--danger' : 'ops-pill'}>{label(PRIORITIES, t.priority)}</span> },
        { key: 'created_at', header: 'Created', render: t => new Date(t.created_at).toLocaleString() },
        {
            key: 'open', header: 'Details',
            render: t => <button type="button" className="btn btn-ghost btn-sm" aria-label={`Open ticket: ${t.subject}`} onClick={() => setSelectedId(t.id)}>Open</button>,
        },
    ];

    const total = load.kind === 'ok' ? load.total : 0;
    const totalPages = Math.max(1, Math.ceil(total / SUPPORT_PAGE_SIZE));

    return (
        <div className="ops-page">
            <PageHeader
                breadcrumb={[{ label: 'Platform' }, { label: 'Support inbox' }]}
                title="Support inbox"
                subtitle="Tickets from every tenant: triage status and priority, and keep internal operator notes."
            />
            <p className="ops-scope">
                Triage only: saving changes status, priority and the internal note. No reply or email is sent to the customer.
            </p>

            <div className="ops-toolbar" role="search" aria-label="Filter support tickets">
                <label className="ops-field">Requester email
                    <input className="form-input" type="search" value={requesterDraft} onChange={e => setRequesterDraft(e.target.value)} />
                </label>
                {tenants && (
                    <label className="ops-field">Tenant
                        <select className="form-input" value={tenantId} onChange={e => setFilter('tenant', e.target.value)}>
                            <option value="">All tenants</option>
                            {[...tenants].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                        </select>
                    </label>
                )}
                <label className="ops-field">Category
                    <select className="form-input" value={category} onChange={e => setFilter('category', e.target.value)}>
                        <option value="">All categories</option>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </label>
                <label className="ops-field">Status
                    <select className="form-input" value={statusParam} onChange={e => setFilter('status', e.target.value)}>
                        <option value="all">All statuses</option>
                        {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </label>
                <label className="ops-field">Priority
                    <select className="form-input" value={priority} onChange={e => setFilter('priority', e.target.value)}>
                        <option value="">All priorities</option>
                        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                </label>
            </div>

            {load.kind === 'loading' && <StateView state="loading" label="Loading tickets" lines={6} />}
            {load.kind === 'error' && (
                <StateView state="error" title="Tickets couldn't be loaded"
                    message="The server did not return the inbox. Nothing below is current."
                    onRetry={() => setReload(n => n + 1)} />
            )}
            {load.kind === 'ok' && load.tickets.length === 0 && (
                narrowed
                    ? <StateView state="filtered-empty" title="No tickets match these filters" onClearFilters={clearFilters} />
                    : status === 'open'
                        ? <StateView state="empty" title="No open tickets" message="The queue is clear."
                            action={<button type="button" className="btn btn-secondary btn-md" onClick={clearFilters}>Show all statuses</button>} />
                        : <StateView state="empty" title="No tickets yet" message="Tickets customers raise from Help & Support appear here." />
            )}
            {load.kind === 'ok' && load.tickets.length > 0 && (
                <>
                    <Table<Row> caption="Support tickets" hideCaption density="compact" columns={columns} data={load.tickets} rowKey={t => t.id} />
                    <nav className="ops-pager" aria-label="Support inbox pages">
                        <span>Page {page} of {totalPages} · {total.toLocaleString()} tickets</span>
                        <span className="ops-pager-buttons">
                            <button type="button" className="btn btn-secondary btn-md" disabled={page <= 1} onClick={() => goToPage(page - 1)}>Previous</button>
                            <button type="button" className="btn btn-secondary btn-md" disabled={page >= totalPages} onClick={() => goToPage(page + 1)}>Next</button>
                        </span>
                    </nav>
                </>
            )}

            {selected && (
                <TicketDrawer key={selected.id} ticket={selected} tenantName={tenantName(selected.tenant_id)}
                    onClose={() => setSelectedId(null)} onSaved={onSaved} />
            )}
        </div>
    );
}

type Save = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved' } | { kind: 'error' };

function TicketDrawer({ ticket, tenantName, onClose, onSaved }: {
    ticket: SupportTicket; tenantName: string; onClose: () => void; onSaved: (t: SupportTicket) => void;
}) {
    const [status, setStatus] = useState(ticket.status);
    const [priority, setPriority] = useState(ticket.priority);
    const [note, setNote] = useState(ticket.admin_notes ?? '');
    const [save, setSave] = useState<Save>({ kind: 'idle' });
    const noteHint = useId();

    const patch: AdminUpdateInput = {};
    if (status !== ticket.status) patch.status = status;
    if (priority !== ticket.priority) patch.priority = priority;
    if (note !== (ticket.admin_notes ?? '')) patch.admin_notes = note;
    const dirty = Object.keys(patch).length > 0;

    const close = () => {
        if (save.kind === 'saving') return;
        if (dirty && !window.confirm('Discard unsaved triage changes?')) return;
        onClose();
    };

    async function submit(e: FormEvent) {
        e.preventDefault();
        setSave({ kind: 'saving' });
        try {
            onSaved(await adminUpdateTicket(ticket.id, patch));
            setSave({ kind: 'saved' });
        } catch {
            setSave({ kind: 'error' }); // the draft stays in the form
        }
    }

    return (
        <Drawer open onClose={close} title={ticket.subject} closeOnBackdrop={false}>
            <dl className="ops-detail">
                <dt>Requester</dt><dd>{ticket.user_email}</dd>
                <dt>Tenant</dt><dd>{tenantName}</dd>
                <dt>Category</dt><dd>{label(CATEGORIES, ticket.category)}</dd>
                <dt>Created</dt><dd>{new Date(ticket.created_at).toLocaleString()}</dd>
                <dt>Updated</dt><dd>{new Date(ticket.updated_at).toLocaleString()}</dd>
                <dt>Ticket ID</dt><dd className="ops-mono">{ticket.id}</dd>
            </dl>

            <section className="ops-section" aria-labelledby="ticket-customer">
                <h3 id="ticket-customer">Customer message</h3>
                <div className="ops-customer">{ticket.body}</div>
                <ContentReportActions ticketId={ticket.id} />
            </section>

            <form onSubmit={submit}>
                <div className="ops-split ops-section">
                    <label className="ops-field">Status
                        <select className="form-input" value={status} onChange={e => setStatus(e.target.value as TicketStatus)}>
                            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </label>
                    <label className="ops-field">Priority
                        <select className="form-input" value={priority} onChange={e => setPriority(e.target.value as TicketPriority)}>
                            {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                    </label>
                </div>

                <section className="ops-internal ops-section" data-testid="internal-note">
                    <h3><label htmlFor="ticket-note">Internal note</label></h3>
                    <span id={noteHint} className="ops-internal-label"><Lock size={12} aria-hidden="true" /> Internal, never shown to the customer</span>
                    <textarea id="ticket-note" className="form-input" rows={4} maxLength={4000}
                        value={note} onChange={e => setNote(e.target.value)} aria-describedby={noteHint} />
                </section>

                <div className="ops-toolbar">
                    <button type="submit" className="btn btn-primary btn-md" disabled={!dirty || save.kind === 'saving'} aria-busy={save.kind === 'saving' || undefined}>
                        {save.kind === 'saving' ? 'Saving…' : 'Save triage'}
                    </button>
                    {save.kind === 'saved' && !dirty && <span role="status" className="ops-inline-ok">Saved.</span>}
                    {save.kind === 'error' && <span role="alert" className="ops-inline-error">Not saved. Your changes are still here; try again.</span>}
                </div>
            </form>
        </Drawer>
    );
}
